import AddCircleIcon from "@mui/icons-material/AddCircle";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { FormikProps } from "formik";
import { uniqBy } from "lodash";
import Flex from "../../../../../../../components/Flex";
import { useFieldLock } from "../../../../../../../components/ReadOnlyFields";
import DeleteIcon from "../../../../../../../components/app/DeleteIcon";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { RosTemplate, RosTemplateType } from "../../../../../../../services";
import { replaceUnderScoreAddSpace } from "../../../../../../../utils/utils";
import PeAddModeDialog from "./PeAddModeDialog";

interface PeFormProps {
  formik: FormikProps<RosTemplate>;
  disabled?: boolean;
}

/**
 * @author [Rajkumar Rathod]
 */
const PeRosForm = ({ formik, disabled = false }: PeFormProps) => {
  // Raw MUI TextField below, so it does not pick up the read-only resolution
  // the shared field components do — resolve it here.
  const lock = useFieldLock(disabled);

  const getFormikKey = (k: keyof RosTemplateType, ind: number) =>
    `rosTemplateTypeSet[${ind}].${k}`;

  const rosTemplates: RosTemplateType[] | undefined = uniqBy(
    formik.values?.rosTemplateTypeSet as any,
    (it) => it.name
  );

  const { handleBlur, handleChange, setFieldValue } = formik;
  const addMoreDisc = useDisclosure();

  const handleDelete = (ind: number) =>
    setFieldValue(getFormikKey("isSelected", ind), false);

  const handleRestore = (ind: number) =>
    setFieldValue(getFormikKey("isSelected", ind), true);
  const showSnackbar = useSnackbar();

  return (
    <Grid container spacing={1} sx={{ flexDirection: "column" }}>
      <PeAddModeDialog
        disclosureHost={addMoreDisc}
        onSave={(vals) => {
          const isDuplicate = rosTemplates?.some(
            (it) => it.name?.toLowerCase() === vals?.name?.toLowerCase()
            // it?.value?.toLowerCase() === vals?.value?.toLowerCase()
          );

          if (isDuplicate) {
            showSnackbar({ type: "error", message: "Template Already Added" });
          } else {
            formik.setFieldValue("rosTemplateTypeSet", [
              ...(rosTemplates ?? []),
              vals,
            ]);
          }

          return new Promise<void>((res, rej) => (isDuplicate ? rej() : res()));
        }}
      />

      {rosTemplates?.map((it, ind) => {
        return (
          it.isSelected && (
            <Grid key={it?.name}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Chip
                  label={replaceUnderScoreAddSpace(it?.name || "-")}
                  sx={{
                    mr: 2,
                    borderRadius: 1,
                    minWidth: "150px",
                    // formik.values.templateTypes === "PHYSICAL"
                    //   ? "150px"
                    //   : "150px",
                  }}
                />
                <Box
                  sx={{
                    flex: 1,
                  }}
                >
                  <TextField
                    disabled={lock.disabled}
                    fullWidth
                    name={getFormikKey("value", ind)}
                    value={it.value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                    size="small"
                    slotProps={{
                      input: { readOnly: lock.readOnly },
                    }}
                  />
                </Box>

                {!disabled && <DeleteIcon onClick={() => handleDelete(ind)} />}
              </Box>
            </Grid>
          )
        );
      })}

      {!disabled && (
        <Flex
          width={"100%"}
          justifyContent={"end"}
          alignItems={"center"}
          my={2}
        >
          <Button variant="outlined" onClick={() => addMoreDisc.open()}>
            Add More
          </Button>
        </Flex>
      )}

      {/* Deleted Templates Section */}
      {!disabled && rosTemplates?.some((it) => !it.isSelected) && (
        <>
          <Grid>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Deleted Templates
            </Typography>
          </Grid>

          {rosTemplates?.map((deleteIt, ind) => {
            return (
              !deleteIt.isSelected && (
                <Grid key={deleteIt.name}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Chip
                      label={replaceUnderScoreAddSpace(deleteIt?.name || "")}
                      sx={{
                        mr: 2,
                        borderRadius: 1,
                        minWidth:
                          formik.values.templateTypes === "PHYSICAL"
                            ? "82px"
                            : "150px",
                      }}
                    />
                    <Box
                      sx={{
                        flex: 1,
                      }}
                    >
                      <TextField
                        fullWidth
                        disabled
                        value={deleteIt?.value}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                    <IconButton
                      color="primary"
                      onClick={() => handleRestore(ind)}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <AddCircleIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              )
            );
          })}
        </>
      )}
    </Grid>
  );
};

export default PeRosForm;
